import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { Calendar } from "./components/ui/calendar";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Checkbox } from "./components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Progress } from "./components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { Mic, MicOff, Volume2, Calendar as CalendarIcon, Brain, Plus, Clock, MapPin, Activity, Heart, Target, TrendingUp, Droplets, Moon, Zap, List } from "lucide-react";
import { format, parseISO } from "date-fns";
import { de } from "date-fns/locale";

// Determine the API base URL.  In production (Netlify) we rely on a
// redirect in `netlify.toml` that proxies all `/api/*` requests to the
// Railway backend.  To support this, if `REACT_APP_BACKEND_URL` is
// undefined we fall back to an empty string so that axios calls
// relative paths like `/api/calendar/events`.  If you set
// `REACT_APP_BACKEND_URL` (e.g. during local development) its value will
// be prepended to the `/api` prefix.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const App = () => {
  // Calendar states
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Voice states
  const [isRecording, setIsRecording] = useState(false);
  const [aiResponse, setAiResponse] = useState("");
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [voiceInput, setVoiceInput] = useState("");
  
  // Tab state
  const [activeTab, setActiveTab] = useState("calendar");
  
  // Fitness states
  const [fitnessActivities, setFitnessActivities] = useState([]);
  const [fitnessStats, setFitnessStats] = useState(null);
  const [healthMetrics, setHealthMetrics] = useState([]);
  const [fitnessGoals, setFitnessGoals] = useState([]);
  const [isFitnessDialogOpen, setIsFitnessDialogOpen] = useState(false);
  const [isHealthDialogOpen, setIsHealthDialogOpen] = useState(false);

  // Yoga program state and dialog
  const [yogaProgram, setYogaProgram] = useState(null);
  const [isYogaProgramDialogOpen, setIsYogaProgramDialogOpen] = useState(false);

  // Options for selecting available equipment for yoga workouts.
  // Keys are lower‑case identifiers which correspond to equipment
  // names used in the backend.  Values indicate whether the user has
  // this equipment available.  You can extend this list to include
  // more props such as "yogakissen" or "yogaklotz".
  const [equipmentOptions, setEquipmentOptions] = useState({
    hanteln: false,
    gummiband: false,
    yogablock: false,
    yogakissen: false,
  });

  // User skill level for yoga workouts.  This can be "beginner",
  // "intermediate" (default) or "advanced".  It influences
  // exercise durations.
  const [userLevel, setUserLevel] = useState("intermediate");

  // State for controlling the yoga program timer.  When
  // `isProgramRunning` is true, the current exercise and remaining
  // time are displayed.  `currentExerciseIndex` points into
  // `yogaProgram.exercises`.  `timeRemaining` is the number of
  // seconds left for the current exercise.  `timerRef` stores the
  // interval ID so it can be cleared when stopping.
  const [isProgramRunning, setIsProgramRunning] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const timerRef = useRef(null);

  // Weekly yoga plan state and dialog.  This holds the weekly plan
  // returned from the backend and controls the visibility of the
  // dialog.
  const [weeklyYogaPlan, setWeeklyYogaPlan] = useState(null);
  const [isWeeklyPlanDialogOpen, setIsWeeklyPlanDialogOpen] = useState(false);
  
  // -- Keine Finanz-Zustände mehr erforderlich --
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    date: format(new Date(), 'yyyy-MM-dd'),
    time: "09:00",
    duration_minutes: 60,
    category: "personal",
    location: "",
    reminder_minutes: 15
  });

  const [newFitnessActivity, setNewFitnessActivity] = useState({
    activity_type: "walking",
    duration_minutes: 30,
    calories_burned: 150,
    steps: null,
    distance_km: null,
    heart_rate_avg: null,
    notes: "",
    date: format(new Date(), 'yyyy-MM-dd'),
    time: "09:00"
  });

  const [newHealthMetrics, setNewHealthMetrics] = useState({
    weight_kg: null,
    sleep_hours: null,
    water_intake_liters: null,
    mood_rating: null,
    energy_level: null,
    stress_level: null,
    notes: "",
    date: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchEvents();
    if (activeTab === "fitness") {
      fetchFitnessData();
    }
  }, [activeTab]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/calendar/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Events:", error);
      toast.error("Fehler beim Laden der Kalender-Events");
    }
  };

  const fetchFitnessData = async () => {
    try {
      const [activitiesRes, statsRes, metricsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/fitness/activities`),
        axios.get(`${API}/fitness/activities/stats`),
        axios.get(`${API}/health/metrics`),
        axios.get(`${API}/fitness/goals`)
      ]);
      
      setFitnessActivities(activitiesRes.data);
      setFitnessStats(statsRes.data);
      setHealthMetrics(metricsRes.data);
      setFitnessGoals(goalsRes.data);
    } catch (error) {
      console.error("Fehler beim Laden der Fitness-Daten:", error);
    }
  };

  // -- Finanz-Funktionen entfernt --

  const createEvent = async () => {
    try {
      await axios.post(`${API}/calendar/events`, newEvent);
      toast.success("Event erfolgreich erstellt!");
      setIsDialogOpen(false);
      setNewEvent({
        title: "",
        description: "",
        date: format(new Date(), 'yyyy-MM-dd'),
        time: "09:00",
        duration_minutes: 60,
        category: "personal",
        location: "",
        reminder_minutes: 15
      });
      fetchEvents();
    } catch (error) {
      console.error("Fehler beim Erstellen des Events:", error);
      toast.error("Fehler beim Erstellen des Events");
    }
  };

  const createFitnessActivity = async () => {
    try {
      await axios.post(`${API}/fitness/activities`, newFitnessActivity);
      toast.success("Fitness-Aktivität erstellt!");
      setIsFitnessDialogOpen(false);
      setNewFitnessActivity({
        activity_type: "walking",
        duration_minutes: 30,
        calories_burned: 150,
        steps: null,
        distance_km: null,
        heart_rate_avg: null,
        notes: "",
        date: format(new Date(), 'yyyy-MM-dd'),
        time: "09:00"
      });
      fetchFitnessData();
    } catch (error) {
      console.error("Fehler beim Erstellen der Aktivität:", error);
      toast.error("Fehler beim Erstellen der Aktivität");
    }
  };

  const createHealthMetrics = async () => {
    try {
      await axios.post(`${API}/health/metrics`, newHealthMetrics);
      toast.success("Gesundheitsdaten gespeichert!");
      setIsHealthDialogOpen(false);
      setNewHealthMetrics({
        weight_kg: null,
        sleep_hours: null,
        water_intake_liters: null,
        mood_rating: null,
        energy_level: null,
        stress_level: null,
        notes: "",
        date: format(new Date(), 'yyyy-MM-dd')
      });
      fetchFitnessData();
    } catch (error) {
      console.error("Fehler beim Speichern der Gesundheitsdaten:", error);
      toast.error("Fehler beim Speichern der Gesundheitsdaten");
    }
  };

  const generateMockData = async () => {
    try {
      const response = await axios.post(`${API}/fitness/generate-mock-data`);
      toast.success("Demo-Daten erfolgreich generiert!");
      fetchFitnessData();
    } catch (error) {
      console.error("Fehler beim Generieren der Demo-Daten:", error);
      toast.error("Fehler beim Generieren der Demo-Daten");
    }
  };

  // Fetch a daily yoga suggestion from the backend.  This will display
  // a toast notification with the suggestion and the recommended
  // start time.  If the request fails, an error toast is shown.
  const getYogaSuggestion = async () => {
    try {
      const response = await axios.get(`${API}/fitness/suggestions/yoga`);
      const { suggestion, recommended_time } = response.data;
      const timeString = new Date(recommended_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      toast.success(`${suggestion}. Empfohlene Zeit: ${timeString}`);
    } catch (error) {
      console.error("Fehler beim Abrufen des Yoga-Tipps:", error);
      toast.error("Yoga-Tipp konnte nicht abgerufen werden");
    }
  };

  // Fetch a daily health suggestion from the backend. This will display
  // a toast notification with the suggestion. If the request fails, an error toast is shown.
  const getHealthSuggestion = async () => {
    try {
      const response = await axios.get(`${API}/health/suggestions/daily`);
      const { suggestion } = response.data;
      toast.success(suggestion);
    } catch (error) {
      console.error("Fehler beim Abrufen des Gesundheitstipps:", error);
      toast.error("Gesundheitstipp konnte nicht abgerufen werden");
    }
  };

  // Fetch the daily yoga program and store it in state.  The
  // request includes optional query parameters for level and
  // equipment.  Equipment is passed as a comma‑separated list of
  // keys which the user has selected in `equipmentOptions`.
  const fetchYogaProgram = async () => {
    try {
      // Build equipment parameter from selected options
      const selectedEquipment = Object.keys(equipmentOptions)
        .filter((key) => equipmentOptions[key])
        .join(',');
      const params = {
        level: userLevel,
        equipment: selectedEquipment || undefined,
      };
      const response = await axios.get(`${API}/fitness/yoga-program/today`, { params });
      setYogaProgram(response.data);
    } catch (error) {
      console.error("Fehler beim Laden des Yoga-Programms:", error);
      toast.error("Yoga-Programm konnte nicht abgerufen werden");
    }
  };

  // Fetch the weekly yoga plan from the backend.  The plan
  // automatically adjusts based on the selected level and equipment.
  const fetchWeeklyYogaPlan = async () => {
    try {
      const selectedEquipment = Object.keys(equipmentOptions)
        .filter((key) => equipmentOptions[key])
        .join(',');
      const params = {
        level: userLevel,
        equipment: selectedEquipment || undefined,
      };
      const response = await axios.get(`${API}/fitness/yoga-program/week`, { params });
      setWeeklyYogaPlan(response.data);
    } catch (error) {
      console.error("Fehler beim Laden des Wochenplans:", error);
      toast.error("Wochenplan konnte nicht abgerufen werden");
    }
  };

  // Start the yoga program.  Initializes state and kicks off
  // progression through each exercise.  If a program is not loaded
  // or is already running, this does nothing.
  const startYogaProgram = () => {
    if (!yogaProgram || isProgramRunning) return;
    setIsProgramRunning(true);
    setCurrentExerciseIndex(0);
    // Begin with the first exercise
    startExercise(0);
  };

  // Advance through exercises.  Schedules a timer for the given
  // exercise index and handles transitions between exercises.  When
  // all exercises have completed, stops the program.  We use
  // SpeechSynthesis to announce the start of each exercise and when
  // ten seconds remain.
  const startExercise = (index) => {
    // If index beyond last exercise, finish
    if (!yogaProgram || index >= yogaProgram.exercises.length) {
      speak("Training abgeschlossen!");
      toast.success("Yoga-Training beendet!");
      setIsProgramRunning(false);
      setTimeRemaining(0);
      return;
    }
    const exercise = yogaProgram.exercises[index];
    setCurrentExerciseIndex(index);
    setTimeRemaining(exercise.duration_seconds);
    // Announce the start of the exercise
    const minutes = Math.round(exercise.duration_seconds / 60);
    speak(`Starte ${exercise.name}. ${minutes > 0 ? minutes + ' Minuten' : exercise.duration_seconds + ' Sekunden'}.`);
    // Clear any existing timer
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;
        // At 10 seconds remaining, give a heads up
        if (newTime === 10) {
          speak(`Noch zehn Sekunden von ${exercise.name}.`);
        }
        // When time is up, move to next exercise
        if (newTime <= 0) {
          clearInterval(timerRef.current);
          startExercise(index + 1);
          return 0;
        }
        return newTime;
      });
    }, 1000);
  };

  // Stop the current yoga program.  Clears the timer and resets
  // relevant state.  A spoken notification indicates the abort.
  const stopYogaProgram = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (isProgramRunning) {
      speak("Yoga-Training abgebrochen.");
      toast.warning("Yoga-Training gestoppt");
    }
    setIsProgramRunning(false);
    setCurrentExerciseIndex(0);
    setTimeRemaining(0);
  };

  const getAIRecommendations = async (userInput) => {
    setIsLoadingAI(true);
    setAiResponse(""); // Clear previous response
    console.log("🤖 Starting AI recommendation request:", userInput);
    
    try {
      const response = await axios.post(`${API}/ai/recommendations`, {
        user_context: userInput,
        request_type: "calendar"
      }, {
        timeout: 30000 // 30 seconds timeout
      });
      
      console.log("🤖 AI Response received:", response.data);
      
      if (response.data && response.data.recommendation) {
        setAiResponse(response.data.recommendation);
        toast.success("KI-Empfehlung erhalten!");
        console.log("✅ AI response set successfully");
      } else {
        console.error("❌ Invalid response structure:", response.data);
        toast.error("Ungültige KI-Antwort erhalten");
      }
    } catch (error) {
      console.error("❌ Fehler bei KI-Empfehlung:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("KI-Anfrage dauerte zu lange - bitte erneut versuchen");
      } else {
        toast.error("Fehler bei der KI-Empfehlung");
      }
    } finally {
      setIsLoadingAI(false);
      console.log("🔄 AI loading state reset");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await uploadAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info("Aufnahme gestartet...");
    } catch (error) {
      console.error("Fehler beim Starten der Aufnahme:", error);
      toast.error("Mikrofon-Zugriff fehlgeschlagen");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.info("Aufnahme beendet, verarbeite...");
    }
  };

  const uploadAudio = async (audioBlob) => {
    try {
      // Check audio blob size
      if (audioBlob.size < 1000) {
        toast.error("Aufnahme zu kurz - bitte länger sprechen");
        return;
      }
      
      if (audioBlob.size > 25 * 1024 * 1024) {
        toast.error("Aufnahme zu lang - bitte kürzer sprechen");
        return;
      }
      
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.wav');
      
      toast.info("Sprache wird verarbeitet...");
      
      const response = await axios.post(`${API}/ai/stt`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000 // 30 seconds timeout
      });
      
      const transcription = response.data.transcription;
      if (!transcription || transcription.trim().length === 0) {
        toast.warning("Keine Sprache erkannt - bitte erneut versuchen");
        return;
      }
      
      setVoiceInput(transcription);
      toast.success(`Sprache erkannt: "${transcription}"`);
      
      // Automatisch KI-Empfehlung anfordern
      await getAIRecommendations(transcription);
    } catch (error) {
      console.error("Fehler bei Speech-to-Text:", error);
      if (error.response?.status === 413) {
        toast.error("Audio-Datei zu groß");
      } else if (error.response?.status === 400) {
        toast.error("Audio-Format nicht unterstützt");
      } else if (error.code === 'ECONNABORTED') {
        toast.error("Spracherkennung dauerte zu lange - bitte erneut versuchen");
      } else {
        toast.error("Spracherkennung fehlgeschlagen");
      }
    }
  };

  const speakText = async (text) => {
    try {
      // Show loading toast
      toast.info("Sprachausgabe wird generiert...");
      
      const response = await axios.post(`${API}/ai/tts`, {
        text: text,
        voice: "nova"
      }, {
        timeout: 25000 // 25 seconds timeout
      });
      
      if (response.data.truncated) {
        toast.warning(`Text wurde gekürzt (${response.data.text_length} Zeichen)`);
      }
      
      const audioData = response.data.audio_base64;
      const audioBlob = new Blob([Uint8Array.from(atob(audioData), c => c.charCodeAt(0))], 
        { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.onplay = () => toast.success("Sprachausgabe gestartet");
        audioRef.current.onended = () => URL.revokeObjectURL(audioUrl); // Clean up
        audioRef.current.onerror = () => toast.error("Fehler bei der Audiowiedergabe");
        audioRef.current.play();
      }
    } catch (error) {
      console.error("Fehler bei Text-to-Speech:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Sprachausgabe dauerte zu lange - Text zu lang?");
      } else {
        toast.error("Sprachausgabe fehlgeschlagen");
      }
    }
  };

  // Quick speech using the browser's SpeechSynthesis API.  This
  // function provides immediate spoken feedback for short
  // messages without calling the backend.  If SpeechSynthesis is
  // unavailable, the call is silently ignored.  We set the
  // language to German.
  const speak = (message) => {
    try {
      if ('speechSynthesis' in window) {
        const utter = new SpeechSynthesisUtterance(message);
        utter.lang = 'de-DE';
        // Cancel any ongoing speech to avoid overlapping messages
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      }
    } catch (err) {
      console.error('Fehler bei SpeechSynthesis:', err);
    }
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return format(eventDate, 'yyyy-MM-dd') === dateStr;
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      personal: "bg-blue-100 text-blue-800",
      work: "bg-purple-100 text-purple-800",
      health: "bg-green-100 text-green-800",
      fitness: "bg-orange-100 text-orange-800"
    };
    return colors[category] || colors.personal;
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      walking: "🚶",
      running: "🏃",
      cycling: "🚴",
      workout: "💪",
      yoga: "🧘"
    };
    return icons[activityType] || "🏃";
  };

  const dayEvents = getEventsForDate(selectedDate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <Toaster position="top-right" />
      <audio ref={audioRef} style={{ display: 'none' }} />
      
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-2">
            KI-Alltagsassistent
          </h1>
          <p className="text-slate-600 text-lg">
            Dein persönlicher Assistent für intelligente Terminplanung und Gesundheit
          </p>
        </div>

        {/* Voice Controls */}
        <div className="flex justify-center mb-8 space-x-4">
          <Button
            onClick={isRecording ? stopRecording : startRecording}
            variant={isRecording ? "destructive" : "default"}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white border-0"
          >
            {isRecording ? (
              <>
                <MicOff className="mr-2 h-5 w-5" />
                Aufnahme stoppen
              </>
            ) : (
              <>
                <Mic className="mr-2 h-5 w-5" />
                Sprachbefehl
              </>
            )}
          </Button>
          
          {aiResponse && (
            <Button
              onClick={() => speakText(aiResponse)}
              variant="outline"
              size="lg"
              className="border-indigo-200 hover:bg-indigo-50"
            >
              <Volume2 className="mr-2 h-5 w-5" />
              Vorlesen
            </Button>
          )}
        </div>

        {/* Voice Input Display */}
        {voiceInput && (
          <Card className="mb-6 border-indigo-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-indigo-700 flex items-center">
                <Mic className="mr-2 h-5 w-5" />
                Deine Spracheingabe
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 italic">"{voiceInput}"</p>
            </CardContent>
          </Card>
        )}

        {/* AI Response */}
        {(aiResponse || isLoadingAI) && (
          <Card className="mb-6 border-purple-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-purple-700 flex items-center">
                <Brain className="mr-2 h-5 w-5" />
                KI-Empfehlung
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingAI ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                  <span>KI analysiert deine Anfrage...</span>
                </div>
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">{aiResponse}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="calendar" className="flex items-center">
              <CalendarIcon className="mr-2 h-4 w-4" />
              Kalender
            </TabsTrigger>
            <TabsTrigger value="fitness" className="flex items-center">
              <Activity className="mr-2 h-4 w-4" />
              Fitness & Gesundheit
            </TabsTrigger>
            {/* Finanzen Tab entfernt */}
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calendar Section */}
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center text-slate-800">
                    <CalendarIcon className="mr-2 h-5 w-5" />
                    Kalender
                  </CardTitle>
                  <CardDescription>
                    Wähle ein Datum um deine Termine zu sehen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={de}
                    className="rounded-md"
                  />
                  
                  <div className="mt-4">
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white border-0">
                          <Plus className="mr-2 h-4 w-4" />
                          Neuer Termin
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[425px]">
                        <DialogHeader>
                          <DialogTitle>Neuen Termin erstellen</DialogTitle>
                          <DialogDescription>
                            Füge einen neuen Termin zu deinem Kalender hinzu
                          </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid gap-2">
                            <Label htmlFor="title">Titel</Label>
                            <Input
                              id="title"
                              value={newEvent.title}
                              onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                              placeholder="Terminbezeichnung"
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="description">Beschreibung</Label>
                            <Textarea
                              id="description"
                              value={newEvent.description}
                              onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                              placeholder="Weitere Details..."
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label htmlFor="date">Datum</Label>
                              <Input
                                id="date"
                                type="date"
                                value={newEvent.date}
                                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="time">Zeit</Label>
                              <Input
                                id="time"
                                type="time"
                                value={newEvent.time}
                                onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                              />
                            </div>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="category">Kategorie</Label>
                            <Select
                              value={newEvent.category}
                              onValueChange={(value) => setNewEvent({...newEvent, category: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="personal">Persönlich</SelectItem>
                                <SelectItem value="work">Arbeit</SelectItem>
                                <SelectItem value="health">Gesundheit</SelectItem>
                                <SelectItem value="fitness">Fitness</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="location">Ort</Label>
                            <Input
                              id="location"
                              value={newEvent.location}
                              onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                              placeholder="Wo findet der Termin statt?"
                            />
                          </div>
                        </div>
                        <Button onClick={createEvent} className="w-full">
                          Termin erstellen
                        </Button>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>

              {/* Events for Selected Date */}
              <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-slate-800">
                    Termine für {format(selectedDate, 'dd. MMMM yyyy', { locale: de })}
                  </CardTitle>
                  <CardDescription>
                    {dayEvents.length} Termine geplant
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {dayEvents.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <CalendarIcon className="mx-auto h-12 w-12 mb-4 opacity-30" />
                      <p>Keine Termine für diesen Tag</p>
                      <p className="text-sm">Nutze die Sprachsteuerung oder erstelle manuell einen Termin</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {dayEvents
                        .sort((a, b) => a.time.localeCompare(b.time))
                        .map((event) => (
                          <div
                            key={event.id}
                            className="p-4 border border-slate-200 rounded-lg hover:shadow-md transition-shadow bg-white"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-semibold text-slate-800">{event.title}</h3>
                              <Badge className={getCategoryColor(event.category)}>
                                {event.category}
                              </Badge>
                            </div>
                            
                            <div className="space-y-2 text-sm text-slate-600">
                              <div className="flex items-center">
                                <Clock className="h-4 w-4 mr-2" />
                                {event.time} ({event.duration_minutes} Min.)
                              </div>
                              
                              {event.location && (
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-2" />
                                  {event.location}
                                </div>
                              )}
                              
                              {event.description && (
                                <p className="text-slate-600 mt-2">{event.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="mt-8 text-center">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-2xl mx-auto">
                <Button
                  variant="outline"
                  onClick={() => getAIRecommendations("Was sollte ich heute tun?")}
                  className="border-blue-200 hover:bg-blue-50"
                >
                  <Brain className="mr-2 h-4 w-4" />
                  Tagesempfehlung
                </Button>
                <Button
                  variant="outline"
                  onClick={() => getAIRecommendations("Plane meine Woche")}
                  className="border-purple-200 hover:bg-purple-50"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  Wochenplanung
                </Button>
                <Button
                  variant="outline"
                  onClick={getHealthSuggestion}
                  className="border-green-200 hover:bg-green-50"
                >
                  <Heart className="mr-2 h-4 w-4" />
                  Gesundheitstipp
                </Button>
                <Button
                  variant="outline"
                  onClick={getYogaSuggestion}
                  className="border-yellow-200 hover:bg-yellow-50"
                >
                  <Zap className="mr-2 h-4 w-4" />
                  Yoga-Tipp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsYogaProgramDialogOpen(true);
                    fetchYogaProgram();
                  }}
                  className="border-orange-200 hover:bg-orange-50"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Yoga-Training
                </Button>
              </div>
            </div>

            {/* Yoga Program Dialog */}
            <Dialog open={isYogaProgramDialogOpen} onOpenChange={setIsYogaProgramDialogOpen}>
              <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                  <DialogTitle>Tägliches Yoga‑Training</DialogTitle>
                  <DialogDescription>
                    Personalisiere dein Programm und starte dann das Training. Viel Spaß!
                  </DialogDescription>
                </DialogHeader>
                {/* Settings for level and equipment */}
                <div className="space-y-4 mb-4">
                  <div className="grid gap-2">
                    <Label htmlFor="level-select" className="font-medium">Level</Label>
                    <Select value={userLevel} onValueChange={setUserLevel} id="level-select">
                      <SelectTrigger>
                        <SelectValue>{userLevel === 'beginner' ? 'Anfänger' : userLevel === 'advanced' ? 'Fortgeschritten' : 'Mittelstufe'}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Anfänger</SelectItem>
                        <SelectItem value="intermediate">Mittelstufe</SelectItem>
                        <SelectItem value="advanced">Fortgeschritten</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label className="font-medium">Equipment</Label>
                    <div className="flex flex-wrap gap-4">
                      {Object.keys(equipmentOptions).map((key) => (
                        <div key={key} className="flex items-center space-x-2">
                          <Checkbox
                            id={`eq-${key}`}
                            checked={equipmentOptions[key]}
                            onCheckedChange={(checked) => setEquipmentOptions({...equipmentOptions, [key]: checked})}
                          />
                          <Label htmlFor={`eq-${key}`} className="capitalize">
                            {key}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={fetchYogaProgram}>Programm aktualisieren</Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        fetchWeeklyYogaPlan();
                        setIsWeeklyPlanDialogOpen(true);
                      }}
                    >
                      Wochenplan anzeigen
                    </Button>
                  </div>
                </div>
                {/* Training state section */}
                {isProgramRunning && yogaProgram ? (
                  <div className="mb-4 p-3 border rounded-lg bg-orange-50">
                    <div className="font-semibold mb-1">Aktuelle Übung:</div>
                    <div className="text-lg font-bold mb-1">
                      {yogaProgram.exercises[currentExerciseIndex]?.name || ''}
                    </div>
                    <div className="text-sm text-slate-600 mb-2">
                      Noch {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')} Minuten
                    </div>
                    <Progress value={
                      yogaProgram.exercises[currentExerciseIndex]
                        ? ((yogaProgram.exercises[currentExerciseIndex].duration_seconds - timeRemaining) / yogaProgram.exercises[currentExerciseIndex].duration_seconds) * 100
                        : 0
                    } className="h-2" />
                    <div className="mt-2 flex gap-2">
                      <Button size="sm" onClick={stopYogaProgram} variant="destructive">Stoppen</Button>
                    </div>
                  </div>
                ) : null}
                {/* Exercises list */}
                {yogaProgram ? (
                  <ul className="space-y-3 mb-4">
                    {yogaProgram.exercises.map((exercise, idx) => (
                      <li key={idx} className={`border rounded-lg p-3 bg-white shadow-sm ${idx === currentExerciseIndex && isProgramRunning ? 'border-orange-400' : ''}`}>
                        <div className="flex items-center gap-3">
                          {exercise.image_url && (
                            <img src={exercise.image_url} alt={exercise.name} className="w-14 h-14 object-cover rounded" />
                          )}
                          <div className="flex-1">
                            <div className="font-semibold">{exercise.name}</div>
                            {exercise.description && (
                              <div className="text-sm text-slate-600">{exercise.description}</div>
                            )}
                            <div className="text-sm text-orange-700 mt-1">Dauer: {Math.floor(exercise.duration_seconds/60)} Min</div>
                            {exercise.equipment && (
                              <div className="text-sm text-indigo-700">Equipment: {exercise.equipment}</div>
                            )}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Lade Yoga-Programm…</p>
                )}
                {/* Start button only visible when program loaded and not running */}
                {!isProgramRunning && yogaProgram && yogaProgram.exercises.length > 0 && (
                  <div className="flex justify-end">
                    <Button onClick={startYogaProgram} className="bg-orange-600 hover:bg-orange-700 text-white">Starten</Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Weekly Yoga Plan Dialog */}
            <Dialog open={isWeeklyPlanDialogOpen} onOpenChange={setIsWeeklyPlanDialogOpen}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Yoga‑Wochenplan</DialogTitle>
                  <DialogDescription>Dein 7‑Tage‑Programm basierend auf deinen Einstellungen.</DialogDescription>
                </DialogHeader>
                {weeklyYogaPlan ? (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                    {weeklyYogaPlan.days.map((dayPlan, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-white shadow-sm">
                        <div className="font-semibold mb-1">
                          {new Date(dayPlan.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </div>
                        <ul className="list-disc list-inside space-y-1">
                          {dayPlan.exercises.map((ex, j) => (
                            <li key={j}>{ex.name}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Lade Wochenplan…</p>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Fitness Tab */}
          <TabsContent value="fitness" className="mt-6">
            <div className="grid lg:grid-cols-3 gap-6">
              
              {/* Fitness Stats Overview */}
              <Card className="lg:col-span-3 bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center text-orange-800">
                    <TrendingUp className="mr-2 h-5 w-5" />
                    Wöchentliche Übersicht
                  </CardTitle>
                  <div className="flex justify-between items-center">
                    <CardDescription>Deine Fitness-Aktivitäten diese Woche</CardDescription>
                    <Button onClick={generateMockData} variant="outline" size="sm">
                      Demo-Daten generieren
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {fitnessStats ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-white rounded-lg">
                        <Activity className="mx-auto h-8 w-8 text-orange-600 mb-2" />
                        <div className="text-2xl font-bold text-orange-600">{fitnessStats.weekly_stats.total_activities}</div>
                        <div className="text-sm text-slate-600">Aktivitäten</div>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg">
                        <Target className="mx-auto h-8 w-8 text-red-600 mb-2" />
                        <div className="text-2xl font-bold text-red-600">{fitnessStats.weekly_stats.total_calories}</div>
                        <div className="text-sm text-slate-600">Kalorien</div>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg">
                        <Clock className="mx-auto h-8 w-8 text-blue-600 mb-2" />
                        <div className="text-2xl font-bold text-blue-600">{fitnessStats.weekly_stats.total_duration_minutes}</div>
                        <div className="text-sm text-slate-600">Minuten</div>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg">
                        <Activity className="mx-auto h-8 w-8 text-green-600 mb-2" />
                        <div className="text-2xl font-bold text-green-600">{fitnessStats.weekly_stats.total_steps}</div>
                        <div className="text-sm text-slate-600">Schritte</div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Activity className="mx-auto h-12 w-12 mb-4 opacity-30" />
                      <p className="text-slate-600">Keine Fitness-Daten vorhanden</p>
                      <p className="text-sm text-slate-500">Erstelle deine erste Aktivität oder generiere Demo-Daten!</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activities */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Activity className="mr-2 h-5 w-5" />
                      Letzte Aktivitäten
                    </span>
                    <Dialog open={isFitnessDialogOpen} onOpenChange={setIsFitnessDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="mr-2 h-4 w-4" />
                          Neue Aktivität
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Neue Fitness-Aktivität</DialogTitle>
                          <DialogDescription>Trage deine Aktivität ein</DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid gap-2">
                            <Label>Aktivitätstyp</Label>
                            <Select
                              value={newFitnessActivity.activity_type}
                              onValueChange={(value) => setNewFitnessActivity({...newFitnessActivity, activity_type: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="walking">Spazieren</SelectItem>
                                <SelectItem value="running">Laufen</SelectItem>
                                <SelectItem value="cycling">Radfahren</SelectItem>
                                <SelectItem value="workout">Workout</SelectItem>
                                <SelectItem value="yoga">Yoga</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label>Dauer (Min)</Label>
                              <Input
                                type="number"
                                value={newFitnessActivity.duration_minutes}
                                onChange={(e) => setNewFitnessActivity({...newFitnessActivity, duration_minutes: parseInt(e.target.value)})}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Kalorien</Label>
                              <Input
                                type="number"
                                value={newFitnessActivity.calories_burned}
                                onChange={(e) => setNewFitnessActivity({...newFitnessActivity, calories_burned: parseInt(e.target.value)})}
                              />
                            </div>
                          </div>
                          <div className="grid gap-2">
                            <Label>Notizen</Label>
                            <Textarea
                              value={newFitnessActivity.notes}
                              onChange={(e) => setNewFitnessActivity({...newFitnessActivity, notes: e.target.value})}
                              placeholder="Wie war das Training?"
                            />
                          </div>
                        </div>
                        <Button onClick={createFitnessActivity}>Aktivität speichern</Button>
                      </DialogContent>
                    </Dialog>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {fitnessActivities.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex items-center p-3 bg-slate-50 rounded-lg">
                        <div className="text-2xl mr-4">{getActivityIcon(activity.activity_type)}</div>
                        <div className="flex-1">
                          <div className="font-medium capitalize">{activity.activity_type}</div>
                          <div className="text-sm text-slate-600">
                            {activity.duration_minutes} Min • {activity.calories_burned} Kalorien
                          </div>
                        </div>
                        <div className="text-xs text-slate-500">
                          {format(new Date(activity.date), 'dd.MM.', { locale: de })}
                        </div>
                      </div>
                    ))}
                    {fitnessActivities.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Activity className="mx-auto h-8 w-8 mb-2 opacity-30" />
                        <p>Keine Aktivitäten gefunden</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Health Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Heart className="mr-2 h-5 w-5" />
                      Gesundheit
                    </span>
                    <Dialog open={isHealthDialogOpen} onOpenChange={setIsHealthDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                          <Plus className="mr-2 h-4 w-4" />
                          Daten
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Gesundheitsdaten erfassen</DialogTitle>
                          <DialogDescription>Trage deine täglichen Werte ein</DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label>Gewicht (kg)</Label>
                              <Input
                                type="number"
                                step="0.1"
                                value={newHealthMetrics.weight_kg || ''}
                                onChange={(e) => setNewHealthMetrics({...newHealthMetrics, weight_kg: parseFloat(e.target.value)})}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Schlaf (Std)</Label>
                              <Input
                                type="number"
                                step="0.5"
                                value={newHealthMetrics.sleep_hours || ''}
                                onChange={(e) => setNewHealthMetrics({...newHealthMetrics, sleep_hours: parseFloat(e.target.value)})}
                              />
                            </div>
                          </div>
                          <div className="grid gap-2">
                            <Label>Wasser (Liter)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={newHealthMetrics.water_intake_liters || ''}
                              onChange={(e) => setNewHealthMetrics({...newHealthMetrics, water_intake_liters: parseFloat(e.target.value)})}
                            />
                          </div>
                          <div className="grid gap-2">
                            <Label>Stimmung (1-10)</Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={newHealthMetrics.mood_rating || ''}
                              onChange={(e) => setNewHealthMetrics({...newHealthMetrics, mood_rating: parseInt(e.target.value)})}
                            />
                          </div>
                        </div>
                        <Button onClick={createHealthMetrics}>Daten speichern</Button>
                      </DialogContent>
                    </Dialog>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {healthMetrics.length > 0 ? (
                    <div className="space-y-4">
                      {healthMetrics.slice(0, 3).map((metric) => (
                        <div key={metric.id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <div className="text-sm font-medium">
                              {format(new Date(metric.date), 'dd.MM.yyyy', { locale: de })}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {metric.weight_kg && (
                              <div className="flex items-center">
                                <Target className="h-3 w-3 mr-1" />
                                {metric.weight_kg} kg
                              </div>
                            )}
                            {metric.sleep_hours && (
                              <div className="flex items-center">
                                <Moon className="h-3 w-3 mr-1" />
                                {metric.sleep_hours} Std
                              </div>
                            )}
                            {metric.water_intake_liters && (
                              <div className="flex items-center">
                                <Droplets className="h-3 w-3 mr-1" />
                                {metric.water_intake_liters} L
                              </div>
                            )}
                            {metric.mood_rating && (
                              <div className="flex items-center">
                                <Heart className="h-3 w-3 mr-1" />
                                Stimmung: {metric.mood_rating}/10
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Heart className="mx-auto h-8 w-8 mb-2 opacity-30" />
                      <p>Keine Gesundheitsdaten</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Fitness Goals */}
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Target className="mr-2 h-5 w-5" />
                    Fitness-Ziele
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {fitnessGoals.length > 0 ? (
                    <div className="grid md:grid-cols-3 gap-4">
                      {fitnessGoals.map((goal) => (
                        <div key={goal.id} className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <div className="font-medium capitalize">{goal.goal_type.replace('_', ' ')}</div>
                            <Badge variant="outline">{goal.unit}</Badge>
                          </div>
                          <div className="mb-2">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Fortschritt</span>
                              <span>{Math.round((goal.current_value / goal.target_value) * 100)}%</span>
                            </div>
                            <Progress value={(goal.current_value / goal.target_value) * 100} className="h-2" />
                          </div>
                          <div className="text-sm text-slate-600">
                            {goal.current_value} / {goal.target_value} {goal.unit}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Target className="mx-auto h-8 w-8 mb-2 opacity-30" />
                      <p>Keine Ziele definiert</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Finanzen Tab entfernt */}
        </Tabs>
      </div>
    </div>
  );
};

export default App;